import { useState } from 'react';
import { TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { ConfirmationModal } from '../../Atoms/ConfirmationModal';
import { Attachment, archiveAttachment, downloadAttachment } from '../../../store/Attachment';
import { getCurrentUser, hasEngineerOrHigherPermissions } from '../../../store/User';
import { getFormattedDate, formatFileSize } from '../../../utils/helpers';

export interface AttachmentItemProps {
  attachment: Attachment;
}

export function AttachmentItem({ attachment }: AttachmentItemProps) {
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const user = getCurrentUser();
  const isOwner = attachment.createdBy?.id != null && attachment.createdBy.id === user?.id;
  const canRemove = isOwner || hasEngineerOrHigherPermissions();

  const handleRemove = async () => {
    setIsArchiving(true);
    try {
      await archiveAttachment(attachment);
    } finally {
      setIsArchiving(false);
    }
  };

  const metadata = [
    formatFileSize(attachment.sizeBytes),
    [attachment.createdBy?.firstName, attachment.createdBy?.lastName].filter(Boolean).join(' '),
    getFormattedDate(attachment.createdAt)
  ]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <div
      className="flex items-center gap-3 py-[9px]"
      data-testid={`attachment-row-${attachment.id}`}>
      <button
        type="button"
        className="shrink-0"
        onClick={() => downloadAttachment(attachment)}
        data-testid="attachment-download-button">
        <TextSpan
          color="indigo"
          size="xs">
          {attachment.filename}
        </TextSpan>
      </button>
      <div className="grow" />
      <TextSpan
        className="shrink-0 whitespace-nowrap"
        color="grey4"
        size="xs">
        {metadata}
      </TextSpan>
      {canRemove && (
        <>
          <button
            type="button"
            disabled={isArchiving}
            onClick={() => setIsConfirmOpen(true)}
            aria-label={`Remove ${attachment.filename}`}
            data-testid="attachment-remove-button">
            <Icon
              type="cancel"
              className="w-3 h-3"
            />
          </button>
          <ConfirmationModal
            open={isConfirmOpen}
            setOpen={setIsConfirmOpen}
            handleSubmit={handleRemove}
            title="Remove Attachment"
            text={`Are you sure you want to remove "${attachment.filename}"?`}
            confirmButtonText="Remove"
          />
        </>
      )}
    </div>
  );
}
